import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";

// Profile DTO matching what the API exposes for the current customer.
export interface CustomerProfileDto {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  /**
   * The version of the Terms this customer has accepted — "August 2026" — or null
   * if they never have. CustomerProvisionGate compares it against the version this
   * build renders and blocks on the acceptance dialog when they differ.
   */
  termsAcceptedVersion?: string | null;
}

const PROFILE_KEY = ["customer-profile"] as const;

export function useCustomerProfile() {
  return useQuery<CustomerProfileDto>({
    queryKey: PROFILE_KEY,
    queryFn: async () => {
      const res = await apiClient.get<CustomerProfileDto>("/customers/me");
      return res.data;
    },
    // Mockup pages still render even when the profile endpoint is unavailable.
    retry: false,
  });
}

export function useUpdateCustomerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<CustomerProfileDto>) => {
      const res = await apiClient.put<CustomerProfileDto>("/customers/me", input);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
    },
  });
}

// Step 1 of the email change: sends a 6-digit verification code to the new address.
export function useRequestEmailChange() {
  return useMutation({
    mutationFn: async (newEmail: string) => {
      await apiClient.post("/customers/me/email/request-code", { newEmail });
    },
  });
}

interface VerifyEmailChangeResponse {
  email: string;
}

// Step 2 of the email change: confirms the code and commits the new email.
export function useVerifyEmailChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const res = await apiClient.post<VerifyEmailChangeResponse>(
        "/customers/me/email/verify-code",
        { code }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
    },
  });
}
